<template>
  <AppBar :title="`${owner}/${repo}`">
    <v-btn
      :to="{
        name: '/repos/[owner]/[repo]/',
        params: { owner, repo },
      }"
      >issues</v-btn
    >
  </AppBar>
  <v-container v-if="issue">
    <IssueHeader
      :issue
      :show-buttons="itemWithButtons === 0"
      @change="changeTitle"
      @hide-buttons="itemWithButtons = undefined"
      @show-buttons="itemWithButtons = 0"
    />
    <p>
      created by {{ issue.user?.login }} <reltime :time="issue.created_at" />
    </p>
    <IssueComment
      :comment="issueAsComment"
      :is-issue-description="true"
      :owner
      :repo
      :show-buttons="itemWithButtons === issue.id"
      @change="changeBody"
      @hide-buttons="itemWithButtons = undefined"
      @show-buttons="itemWithButtons = issue.id"
    />
  </v-container>
  <v-container>
    <IssueLabels
      :index
      :labels="issue?.labels ?? []"
      :owner
      :repo
      @change="invalidateQueries"
    />
  </v-container>
  <v-sheet v-for="c of comments" :key="c.id || ''" border class="">
    <v-container>
      <IssueComment
        v-if="c.type === 'comment'"
        :comment="c"
        :is-issue-description="false"
        :owner
        :repo
        :show-buttons="itemWithButtons === c.id"
        @change="(comment: string) => changeComment(c.id, comment)"
        @hide-buttons="itemWithButtons = undefined"
        @show-buttons="itemWithButtons = c.id"
      />
      <p v-else-if="c.type === 'label'">
        {{ c.user?.login }} <span v-if="c.body">added</span
        ><span v-else>removed</span> the label
        <v-chip density="compact">{{ c.label?.name }}</v-chip>
        <reltime :time="c.created_at" />.
      </p>
      <p v-else-if="c.type === 'pin'">
        {{ c.user?.login }} pinned this issue <reltime :time="c.created_at" />.
      </p>
      <p v-else-if="c.type === 'change_title'">
        {{ c.user?.login }} changed the title of this issue
        <reltime :time="c.created_at" />.
      </p>
      <p v-else-if="c.type === 'close'">
        {{ c.user?.login }} closed this issue <reltime :time="c.created_at" />.
      </p>
      <p v-else-if="c.type === 'reopen'">
        {{ c.user?.login }} reopened this issue
        <reltime :time="c.created_at" />.
      </p>
      <p v-else>
        {{ c.user?.login }} {{ c.type }} this issue
        <reltime :time="c.created_at" />.
      </p>
    </v-container>
  </v-sheet>
  <v-container>
    <AddComment
      :closed="issue?.state === 'closed'"
      :open="issue?.state === 'open'"
      @close="
        (comment: string, files: File[]) =>
          changeIssueState('closed', comment, files)
      "
      @comment="sendComment"
      @reopen="
        (comment: string, files: File[]) =>
          changeIssueState('open', comment, files)
      "
    />
  </v-container>
</template>
<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import {
  type Comment,
  type EditIssueOption,
  issueCreateComment,
  issueCreateIssueCommentAttachment,
  useIssueEditComment,
  useIssueEditIssue,
  useIssueGetCommentsAndTimeline,
  useIssueGetIssue,
} from "../../../../../../gen/forgejo-vue";

const props = defineProps<{
  owner: string;
  repo: string;
  index: number;
}>();

const itemWithButtons = ref();
const { data: issueData, queryKey: issueQueryKey } = useIssueGetIssue(
  props.owner,
  props.repo,
  props.index,
);
const issue = computed(() => issueData.value?.data);
const issueAsComment = computed((): Comment => {
  const v = issueData.value?.data;
  if (v) {
    return {
      ...(v.body !== undefined && { body: v.body }),
      ...(v.created_at !== undefined && { created_at: v.created_at }),
      ...(v.html_url !== undefined && { html_url: v.html_url }),
      ...(v.user !== undefined && { user: v.user }),
    };
  }
  return {};
});
const { data: commentsData, queryKey: commentsQueryKey } =
  useIssueGetCommentsAndTimeline(props.owner, props.repo, props.index);
const comments = computed(() => commentsData.value?.data);

const mutateOptions = { mutation: { onSuccess: invalidateQueries } };
const { mutate: mutateIssue } = useIssueEditIssue(mutateOptions);
const { mutate: editComment } = useIssueEditComment(mutateOptions);

async function sendComment(comment: string, assets: File[]) {
  if (comment) {
    const r = await issueCreateComment(props.owner, props.repo, props.index, {
      body: comment,
    });
    if (typeof r.data.id === "number") {
      for (const file of assets) {
        await issueCreateIssueCommentAttachment(
          props.owner,
          props.repo,
          r.data.id,
          {
            attachment: file,
          },
        );
      }
    }
    await invalidateQueries();
  }
}

function changeIssue(data: EditIssueOption) {
  mutateIssue({
    owner: props.owner,
    repo: props.repo,
    index: props.index,
    data,
  });
}

function changeBody(body: string) {
  changeIssue({ body });
}

function changeTitle(title: string) {
  changeIssue({ title });
}

async function changeIssueState(
  state: "open" | "closed",
  comment: string,
  files: File[],
) {
  await sendComment(comment, files);
  changeIssue({ state });
}

function changeComment(id: number | undefined, comment: string) {
  if (id !== undefined) {
    editComment({
      owner: props.owner,
      repo: props.repo,
      id,
      data: { body: comment },
    });
  }
}

const queryClient = useQueryClient();
async function invalidateQueries() {
  await queryClient.invalidateQueries({
    queryKey: issueQueryKey,
  });
  await queryClient.invalidateQueries({
    queryKey: commentsQueryKey,
  });
}
</script>
